import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Upload, Calendar, User, Tag, LogOut } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LoginForm from '../components/LoginForm';

interface NewsItem {
  id: number;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  date: string;
  category: string;
  image: string;
  views: number;
  likes: number;
  tags: string[];
  relatedLink: string;
}

interface NewsFormData {
  title: string;
  content: string;
  excerpt: string;
  author: string;
  category: string;
  tags: string;
  relatedLink: string;
  image: File | null;
}

const AdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<NewsFormData>({
    title: '',
    content: '',
    excerpt: '',
    author: 'CHAIR 연구센터',
    category: '공지사항',
    tags: '',
    relatedLink: '',
    image: null
  });

  const categories = ['공지사항', '행사', '연구성과', '교육', '협력', '시설'];

  const getToken = () => localStorage.getItem('admin_token') || '';

  // 컴포넌트 마운트 시 토큰 유효성 확인
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      fetch('/api/admin/verify', { headers: { 'x-admin-token': token } })
        .then(res => {
          if (res.ok) {
            setIsAuthenticated(true);
            loadNewsData();
          } else {
            localStorage.removeItem('admin_token');
          }
        })
        .catch(() => localStorage.removeItem('admin_token'));
    }
  }, []);

  // 로그인 처리
  const handleLogin = async (password: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/admin/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();
      if (response.ok && data.token) {
        setIsAuthenticated(true);
        localStorage.setItem('admin_token', data.token);
        loadNewsData();
        return true;
      }
      if (data.error) alert(data.error);
      return false;
    } catch (error) {
      console.error('비밀번호 검증 오류:', error);
      return false;
    }
  };

  // 로그아웃 처리
  const handleLogout = () => {
    const token = getToken();
    if (token) {
      fetch('/api/admin/logout', { method: 'POST', headers: { 'x-admin-token': token } }).catch(() => {});
    }
    setIsAuthenticated(false);
    localStorage.removeItem('admin_token');
    setNewsItems([]);
  };

  const loadNewsData = async () => {
    try {
      const response = await fetch('/api/news');
      const data = await response.json();
      setNewsItems(data);
    } catch (error) {
      console.error('뉴스 데이터 로드 실패:', error);
      // 오류 시 기본 데이터 사용
      const sampleData: NewsItem[] = [
        {
          id: 1,
          title: "인간-AI 공존 교육 헌장 제정식 개최",
          content: "서울여자대학교 인간-AI 협력 연구센터(CHAIR)에서 인간과 AI가 조화롭게 공존하는 미래를 위한 교육 헌장을 제정하고 공식 발표식을 개최했습니다.",
          excerpt: "인간과 AI가 조화롭게 공존하는 미래를 위한 교육 헌장 제정식이 성공적으로 개최되었습니다.",
          author: "CHAIR 연구센터",
          date: "2025-10-15",
          category: "공지사항",
          image: "/chair-logo.png",
          views: 156,
          likes: 23,
          tags: ["헌장", "교육", "AI윤리"],
          relatedLink: "https://chair.swu.ac.kr/charter"
        },
        {
          id: 2,
          title: "CHAIR 연구센터 신규 연구실 개소식",
          content: "인간-AI 협력 연구센터의 신규 연구실 개소식이 성대하게 개최되었습니다.",
          excerpt: "CHAIR 연구센터의 신규 연구실 개소식이 성대하게 개최되었습니다.",
          author: "관리팀",
          date: "2025-08-10",
          category: "시설",
          image: "/chair-logo.png",
          views: 167,
          likes: 28,
          tags: ["연구실", "개소식", "시설"],
          relatedLink: ""
        }
      ];
      setNewsItems(sampleData);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('Form submitted!', formData);

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('content', formData.content);
    formDataToSend.append('excerpt', formData.excerpt);
    formDataToSend.append('author', formData.author);
    formDataToSend.append('category', formData.category);
    formDataToSend.append('tags', formData.tags);
    formDataToSend.append('relatedLink', formData.relatedLink);

    if (formData.image) {
      formDataToSend.append('image', formData.image);
    }

    try {
      const url = isEditing && editingId 
        ? `/api/news/${editingId}`
        : '/api/news';
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'x-admin-token': getToken() },
        body: formDataToSend
      });
      
      if (response.ok) {
        const newNewsItem = await response.json();

        console.log('News saved successfully:', newNewsItem);

        if (isEditing && editingId) {
          setNewsItems(prev => prev.map(item => item.id === editingId ? newNewsItem : item));
        } else {
          setNewsItems(prev => [newNewsItem, ...prev]);
        }

        // 폼 리셋
        setFormData({
          title: '',
          content: '',
          excerpt: '',
          author: 'CHAIR 연구센터',
          category: '공지사항',
          tags: '',
          relatedLink: '',
          image: null
        });
        setShowForm(false);
        setIsEditing(false);
        setEditingId(null);

        alert('새소식이 성공적으로 등록되었습니다!');
      } else {
        const errorData = await response.text();
        console.error('뉴스 저장 실패:', errorData);
        alert('새소식 등록에 실패했습니다: ' + errorData);
      }
    } catch (error) {
      console.error('API 호출 실패:', error);
      alert('API 호출 중 오류가 발생했습니다: ' + error);
    }
  };

  const handleEdit = (item: NewsItem) => {
    setFormData({
      title: item.title,
      content: item.content,
      excerpt: item.excerpt,
      author: item.author,
      category: item.category,
      tags: item.tags.join(', '),
      relatedLink: item.relatedLink || '',
      image: null
    });
    setIsEditing(true);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('정말로 이 새소식을 삭제하시겠습니까?')) {
      try {
        const response = await fetch(`/api/news/${id}`, {
          method: 'DELETE',
          headers: { 'x-admin-token': getToken() }
        });
        
        if (response.ok) {
          setNewsItems(prev => prev.filter(item => item.id !== id));
        } else {
          console.error('뉴스 삭제 실패');
        }
      } catch (error) {
        console.error('API 호출 실패:', error);
      }
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 인증되지 않은 경우 로그인 폼 표시
  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="h-16" />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-teal-500 to-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">
            새소식 관리
          </h1>
          <p className="text-xl text-teal-100 max-w-3xl mx-auto">
            CHAIR 연구센터의 새소식을 관리하고 편집하세요
          </p>
        </div>
      </section>

      {/* Admin Controls */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">새소식 목록</h2>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center px-4 py-2 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors"
              >
                <Plus className="h-5 w-5 mr-2" />
                새 소식 추가
              </button>
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                <LogOut className="h-5 w-5 mr-2" />
                로그아웃
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* News Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  {isEditing ? '새소식 편집' : '새 소식 추가'}
                </h3>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setIsEditing(false);
                    setEditingId(null);
                    setFormData({
                      title: '',
                      content: '',
                      excerpt: '',
                      author: 'CHAIR 연구센터',
                      category: '공지사항',
                      tags: '',
                      relatedLink: '',
                      image: null
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* 제목 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    제목 *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="새소식 제목을 입력하세요"
                  />
                </div>

                {/* 요약 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    요약 *
                  </label>
                  <textarea
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="새소식 요약을 입력하세요"
                  />
                </div>

                {/* 내용 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    내용 *
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="새소식 내용을 입력하세요"
                  />
                </div>

                {/* 작성자 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    작성자 *
                  </label>
                  <input
                    type="text"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="작성자명을 입력하세요"
                  />
                </div>

                {/* 카테고리 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    카테고리 *
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                {/* 태그 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    태그 (쉼표로 구분)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="예: 헌장, 교육, AI윤리"
                  />
                </div>

                {/* 관련 링크 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    관련 링크 (선택사항)
                  </label>
                  <input
                    type="url"
                    name="relatedLink"
                    value={formData.relatedLink}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="https://example.com"
                  />
                </div>

                {/* 이미지 업로드 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    대표 이미지
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                    />
                    <label
                      htmlFor="image-upload"
                      className="cursor-pointer inline-flex items-center px-4 py-2 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors"
                    >
                      <Upload className="h-5 w-5 mr-2" />
                      이미지 선택
                    </label>
                    {formData.image && (
                      <div className="mt-4">
                        <img
                          src={URL.createObjectURL(formData.image)}
                          alt="미리보기"
                          className="max-w-xs mx-auto rounded-lg"
                        />
                        <p className="text-sm text-gray-600 mt-2">{formData.image.name}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 버튼 */}
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors"
                  >
                    {isEditing ? '수정' : '추가'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* News List */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6">
            {newsItems.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-3">
                      <span className="bg-teal-50 text-teal-800 px-3 py-1 rounded-full text-sm font-medium">
                        {item.category}
                      </span>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-1" />
                        <span>{formatDate(item.date)}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <User className="h-4 w-4 mr-1" />
                        <span>{item.author}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4">
                      {item.excerpt}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {item.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs"
                        >
                          <Tag className="h-3 w-3 inline mr-1" />
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    {item.relatedLink && (
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">관련 링크</p>
                        <a 
                          href={item.relatedLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 break-all"
                        >
                          {item.relatedLink}
                        </a>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Eye className="h-4 w-4 mr-1" />
                        <span>{item.views}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(item)}
                      className="p-2 text-teal-600 hover:bg-teal-50/60 rounded-lg transition-colors"
                      title="편집"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="삭제"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdminPage;
